/**
 * diff.ts — minimal line diff for the copilot's edit preview (PURE — verified
 * under Node). LCS over lines; adjacent changes group into hunks the UI renders
 * as red/green blocks with per-edit Apply/Reject.
 */

export interface DiffLine { kind: 'same' | 'del' | 'add'; text: string }
export interface DiffHunk { header: string; lines: DiffLine[] }

/** Line-level LCS diff (O(n·m) — script files are small; cap guards pathology). */
export function diffLines(a: string, b: string): DiffLine[] {
  const A = a.split('\n');
  const B = b.split('\n');
  if (A.length * B.length > 4_000_000) {
    // Degenerate guard: whole-file replace for enormous inputs.
    return [...A.map((t) => ({ kind: 'del' as const, text: t })), ...B.map((t) => ({ kind: 'add' as const, text: t }))];
  }
  // LCS table
  const n = A.length, m = B.length;
  const dp = new Uint32Array((n + 1) * (m + 1));
  const at = (i: number, j: number): number => dp[i * (m + 1) + j];
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i * (m + 1) + j] = A[i] === B[j] ? at(i + 1, j + 1) + 1 : Math.max(at(i + 1, j), at(i, j + 1));
    }
  }
  const out: DiffLine[] = [];
  let i = 0, j = 0;
  while (i < n && j < m) {
    if (A[i] === B[j]) { out.push({ kind: 'same', text: A[i] }); i++; j++; }
    else if (at(i + 1, j) >= at(i, j + 1)) { out.push({ kind: 'del', text: A[i] }); i++; }
    else { out.push({ kind: 'add', text: B[j] }); j++; }
  }
  while (i < n) out.push({ kind: 'del', text: A[i++] });
  while (j < m) out.push({ kind: 'add', text: B[j++] });
  return out;
}

/** Group a line diff into hunks (changed runs + `context` lines around them). */
export function toHunks(lines: DiffLine[], context = 2): DiffHunk[] {
  const hunks: DiffHunk[] = [];
  let aLine = 1;
  // indices of changed lines
  const changed = lines.map((l, idx) => (l.kind === 'same' ? -1 : idx)).filter((x) => x >= 0);
  if (changed.length === 0) return hunks;
  let start = Math.max(0, changed[0] - context);
  let end = Math.min(lines.length - 1, changed[0] + context);
  const flush = (s: number, e: number): void => {
    // compute the a-file line number of the hunk start
    let ln = 1;
    for (let k = 0; k < s; k++) if (lines[k].kind !== 'add') ln++;
    hunks.push({ header: `@ line ${ln}`, lines: lines.slice(s, e + 1) });
  };
  for (let c = 1; c < changed.length; c++) {
    const idx = changed[c];
    if (idx - end <= context * 2) { end = Math.min(lines.length - 1, idx + context); continue; }
    flush(start, end);
    start = Math.max(0, idx - context);
    end = Math.min(lines.length - 1, idx + context);
  }
  flush(start, end);
  void aLine;
  return hunks;
}

/** Stats line for the UI ("+12 −4"). */
export function diffStats(lines: DiffLine[]): { added: number; removed: number } {
  let added = 0, removed = 0;
  for (const l of lines) { if (l.kind === 'add') added++; else if (l.kind === 'del') removed++; }
  return { added, removed };
}
